import { withStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import Styles from 'pages/licenses/styles/LListStyles';
import React, { useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
} from 'react-query';
import { useHistory } from 'react-router-dom';
import DashStyles from 'styles/dashboard';
import combineStyles from 'utils/combineStyles';

import { convertHtmlToReact } from '@hedgedoc/html-to-react';

import Loading from 'components/loading/Loading';
import {
  getAllBespokeLicenses,
  postSignLicense,
} from 'services/educationalLicenseService';

interface SignLicenseProps {
  classes: any;
  refetchLicenseStatus: any;
}

const queryClient = new QueryClient();

const SignLicenseWrapper: React.FC<SignLicenseProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <SignLicense {...props} />
    </QueryClientProvider>
  );
};

const SignLicense: React.FC<SignLicenseProps> = ({
  classes,
  refetchLicenseStatus,
}) => {
  const [agree, setAgree] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const history = useHistory();

  const {
    data: templates,
    isLoading,
    isError,
    error,
  } = useQuery(['bespokeLicenses'], getAllBespokeLicenses);

  const signLicenseMutation = useMutation(postSignLicense, {
    onSuccess: () => {
      refetchLicenseStatus().then(() => {
        history.push('/catalogue');
      });
    },
    onError: (err) => {
      console.error('Error signing license:', err);
    },
  });

  const handleClose = () => {
    if (agree) {
      signLicenseMutation.mutate();
    }
  };

  const handleAgreeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgree(event.target.checked);
  };

  const handleTemplateChange = (index: number) => {
    setSelectedTemplateIndex(index);
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className={classes.headerContainer}>
        <Typography
          className={classes.dashboardTitle}
          variant='h4'
          fontWeight={700}
        >
          Sign License Agreements
        </Typography>
      </div>
      <Box className={classes.modalStyle}>
        <Typography className={classes.descriptionText} variant='body1'>
          Please read carefully before agreeing. Ensure you understand all terms
          and conditions under which our project partners have granted access.
        </Typography>
        {templates && templates.length > 1 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            {templates.map((template: any, index: number) => (
              <Button
                key={template.id}
                variant={
                  selectedTemplateIndex === index ? 'contained' : 'outlined'
                }
                className={
                  selectedTemplateIndex === index
                    ? classes.buttonStyle
                    : classes.buttonStyleContracts
                }
                onClick={() => handleTemplateChange(index)}
                sx={{ mx: 1 }}
              >
                {template.title}
              </Button>
            ))}
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
          }}
        >
          {templates && templates.length > 0 && (
            <div className={classes.htmlViewerStyle}>
              {convertHtmlToReact(templates[selectedTemplateIndex]?.preview)}
            </div>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agree}
                className={classes.checkboxStyle}
                onChange={handleAgreeChange}
              />
            }
            label='I agree with all the license agreements and terms and conditions when using the Archive Material in your film.'
          />
        </Box>
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            variant='contained'
            className={classes.buttonStyle}
            onClick={handleClose}
            disabled={!agree || signLicenseMutation.isLoading}
          >
            {signLicenseMutation.isLoading
              ? 'Submitting...'
              : 'Agree and Close'}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

const combinedStyles = combineStyles(DashStyles, Styles);

export default withStyles(combinedStyles as any)(SignLicenseWrapper);
